import { useQuery } from '@tanstack/react-query'
import { productsApi } from '@/api/endpoints/products'

interface UseProductsParams {
  search?: string
  status?: 'all' | 'active' | 'inactive'
  page?: number
  pageSize?: number
  restaurantId?: number
  categoryCode?: string
  openPrice?: boolean
  /** CHG-2026-09-duplicate-product-merge: drop merged duplicates (a picker that must not sell one). */
  excludeMerged?: boolean
  /** CHG-2026-09-duplicate-product-merge: keep only webbill-imported products (the merge dialog's primary picker). */
  webbillOnly?: boolean
  /** CHG-2026-09-stock-usage-and-price-twins: only products that can be a dish's part. */
  stockArticlesOnly?: boolean
}

/**
 * `options.enabled` keeps a caller from firing the request until it actually wants it — the count
 * search sheet only queries the wider catalogue when the counter explicitly asks, never per
 * keystroke. It stays out of the query key, so it cannot fragment the cache.
 */
export function useProducts(
  params?: UseProductsParams,
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: ['products', params],
    queryFn: () => productsApi.list(params),
    enabled: options?.enabled ?? true,
  })
}

/**
 * Resolve a known set of SKUs to products, scoped to a restaurant.
 *
 * Prefer this over `useProducts` with a large `pageSize` whenever the SKUs are known up front:
 * the list endpoint is SKU-ordered and paged, so a lookup built on it silently misses every SKU
 * past the page boundary.
 */
export function useProductLookup(
  skus: Array<string>,
  restaurantId?: number,
  /** Ask for `availableQty` per product — only ever true for a caller holding `inventory:view`. */
  includeStock = false,
) {
  // Sorted so the cache key is stable regardless of the order the SKUs were collected in.
  const key = [...skus].sort()
  return useQuery({
    // `includeStock` belongs in the key: the same SKUs answer differently with and without it.
    queryKey: ['products', 'lookup', restaurantId, includeStock, key],
    queryFn: () => productsApi.lookup(key, restaurantId, includeStock),
    enabled: key.length > 0 && restaurantId !== undefined,
  })
}
